'use client';

import React from 'react';
import { Box, Flex, GridItem, Text, VStack } from '@skin-seoul/design-system';
import { Suspense } from 'react';
import { getPriceProductBasicInfo, type ProductSortBy, getProductType, type Product } from '@skin-seoul/service';
import ProductCard from '@/components/products/ProductCard';
import { CollectionsProductGrid, CollectionsProductGridSkeleton } from '../CollectionsProductGrid';
import { type FilterOptions, type FilterSummary } from '../../types/filters';
import { useProductGridData } from '../../hooks/useProductGridData';
import { useSearchProductGridData } from '../../hooks/useSearchProductGridData';
import { useCollectionProductGridData } from '../../hooks/useCollectionProductGridData';
import { useProductDisplay } from '../../hooks/shared/useProductDisplay';
import { isProductsEmpty } from '../../utils/collectionProductDataHelpers';
import { QueryBoundary } from '@/components/ui/QueryBoundary';
import ProductCompareSection from '@/components/products/ProductCompareSection/ProductCompareSection';
interface ProductsGridSectionProps {
  category?: string;
  subcategory?: string;
  subSubcategory?: string;
  brand?: string;
  productTag?: string;
  searchQuery?: string;
  collectionName?: string;
  filters?: FilterOptions;
  sortBy?: ProductSortBy;
  currentPage?: number;
  productsPerPage: number;
  onProductCountChange?: (count: number) => void;
  onFilterSummaryChange?: (summary: FilterSummary | undefined) => void;
  accumulatedProducts?: Product[];
  accumulatedReviewStatsMap?: Record<string, unknown>;
  onProductsLoaded?: (products: Product[], reviewStatsMap: Record<string, unknown>) => void;
}
interface ProductGridContentProps {
  products: Product[];
  reviewStatsMap: Record<string, unknown>;
  emptyStateMessage: string;
  sortBy?: ProductSortBy;
}
const PRODUCT_COMPARE_INSERT_POSITION = 6;
const ProductGridContent = ({
  products,
  reviewStatsMap,
  emptyStateMessage,
  sortBy
}: ProductGridContentProps) => {
  if (isProductsEmpty(products)) {
    return <Flex width="100%" justifyContent="center" alignItems="center" padding="60px 0">
        <Text color="neutral.60" textStyle="body2" fontWeight="medium" textAlign="center">
          {emptyStateMessage}
        </Text>
      </Flex>;
  }
  const shouldShowCompareSection = products.length > PRODUCT_COMPARE_INSERT_POSITION;
  return <VStack width="100%" gap={8} data-sentry-element="VStack" data-sentry-component="ProductGridContent" data-sentry-source-file="ProductsGridSection.tsx">
      <CollectionsProductGrid data-sentry-element="CollectionsProductGrid" data-sentry-source-file="ProductsGridSection.tsx">
        {products.slice(0, PRODUCT_COMPARE_INSERT_POSITION).map(product => {
        const productInfo = getPriceProductBasicInfo(product, sortBy);
        const productType = getProductType(product);
        const productReviewStats = reviewStatsMap[product.productId] as {
          averageRate?: number;
          totalReviews?: number;
        } | undefined;
        return <GridItem key={product.productId} minWidth={0}>
              <ProductCard key={product.productId} href={product.productLink} productType={productType} name={product.name} brand={product.brand} productImage={product.productImageUrls[0]} sku={product.sku} currency={product.currency} originalPrice={productInfo.originalPrice} salePrice={productInfo.salePrice} rating={productReviewStats?.averageRate} reviewCount={productReviewStats?.totalReviews} productId={product.productId.toString()} category={product.category} />
            </GridItem>;
      })}
      </CollectionsProductGrid>

      {shouldShowCompareSection && <ProductCompareSection containerProps={{
      width: '100%',
      background: 'white',
      borderRadius: '8px'
    }} />}

      {shouldShowCompareSection && <CollectionsProductGrid>
          {products.slice(PRODUCT_COMPARE_INSERT_POSITION).map(product => {
        const productInfo = getPriceProductBasicInfo(product, sortBy);
        const productType = getProductType(product);
        const productReviewStats = reviewStatsMap[product.productId] as {
          averageRate?: number;
          totalReviews?: number;
        } | undefined;
        return <GridItem key={product.productId} minWidth={0}>
                <ProductCard key={product.productId} href={product.productLink} productType={productType} name={product.name} brand={product.brand} productImage={product.productImageUrls[0]} sku={product.sku} currency={product.currency} originalPrice={productInfo.originalPrice} salePrice={productInfo.salePrice} rating={productReviewStats?.averageRate} reviewCount={productReviewStats?.totalReviews} productId={product.productId.toString()} category={product.category} />
              </GridItem>;
      })}
        </CollectionsProductGrid>}
    </VStack>;
};
const SearchProductsGridContent = ({
  searchQuery,
  filters,
  sortBy,
  currentPage,
  productsPerPage,
  onProductCountChange,
  onFilterSummaryChange,
  accumulatedProducts,
  accumulatedReviewStatsMap,
  onProductsLoaded
}: Omit<ProductsGridSectionProps, 'category' | 'subcategory' | 'brand'> & {
  searchQuery: string;
}) => {
  const {
    products,
    reviewStatsMap
  } = useSearchProductGridData({
    searchQuery,
    filters,
    sortBy,
    currentPage,
    productsPerPage,
    onProductCountChange,
    onFilterSummaryChange
  });
  const {
    displayProducts,
    displayReviewStatsMap
  } = useProductDisplay({
    products,
    reviewStatsMap,
    accumulatedProducts,
    accumulatedReviewStatsMap,
    onProductsLoaded
  });
  return <ProductGridContent products={displayProducts} reviewStatsMap={displayReviewStatsMap} emptyStateMessage="No products found for this search" sortBy={sortBy} data-sentry-element="ProductGridContent" data-sentry-component="SearchProductsGridContent" data-sentry-source-file="ProductsGridSection.tsx" />;
};
const BrowseProductsGridContent = ({
  category,
  subcategory,
  subSubcategory,
  brand,
  productTag,
  filters,
  sortBy,
  currentPage,
  productsPerPage,
  onProductCountChange,
  onFilterSummaryChange,
  accumulatedProducts,
  accumulatedReviewStatsMap,
  onProductsLoaded
}: Omit<ProductsGridSectionProps, 'searchQuery' | 'collectionName'>) => {
  const {
    products,
    reviewStatsMap
  } = useProductGridData({
    category,
    subcategory,
    subSubcategory,
    brand,
    productTag,
    filters,
    sortBy,
    currentPage,
    productsPerPage,
    onProductCountChange,
    onFilterSummaryChange
  });
  const {
    displayProducts,
    displayReviewStatsMap
  } = useProductDisplay({
    products,
    reviewStatsMap,
    accumulatedProducts,
    accumulatedReviewStatsMap,
    onProductsLoaded
  });
  return <ProductGridContent products={displayProducts} reviewStatsMap={displayReviewStatsMap} emptyStateMessage="No products found for this collection" sortBy={sortBy} data-sentry-element="ProductGridContent" data-sentry-component="BrowseProductsGridContent" data-sentry-source-file="ProductsGridSection.tsx" />;
};
const CollectionProductsGridContent = ({
  collectionName,
  currentPage,
  productsPerPage,
  onProductCountChange,
  onFilterSummaryChange,
  accumulatedProducts,
  accumulatedReviewStatsMap,
  onProductsLoaded,
  sortBy
}: {
  collectionName: string;
  currentPage?: number;
  productsPerPage: number;
  onProductCountChange?: (count: number) => void;
  onFilterSummaryChange?: (summary: FilterSummary | undefined) => void;
  accumulatedProducts?: Product[];
  accumulatedReviewStatsMap?: Record<string, unknown>;
  onProductsLoaded?: (products: Product[], reviewStatsMap: Record<string, unknown>) => void;
  sortBy?: ProductSortBy;
}) => {
  const {
    products,
    reviewStatsMap
  } = useCollectionProductGridData({
    collectionName,
    currentPage,
    productsPerPage,
    onProductCountChange,
    onFilterSummaryChange
  });
  const {
    displayProducts,
    displayReviewStatsMap
  } = useProductDisplay({
    products,
    reviewStatsMap,
    accumulatedProducts,
    accumulatedReviewStatsMap,
    onProductsLoaded
  });
  return <ProductGridContent products={displayProducts} reviewStatsMap={displayReviewStatsMap} emptyStateMessage="No products found for this collection" sortBy={sortBy} data-sentry-element="ProductGridContent" data-sentry-component="CollectionProductsGridContent" data-sentry-source-file="ProductsGridSection.tsx" />;
};
const ProductsGridSection = ({
  category,
  subcategory,
  subSubcategory,
  brand,
  productTag,
  searchQuery,
  collectionName,
  filters,
  sortBy,
  currentPage,
  productsPerPage,
  onProductCountChange,
  onFilterSummaryChange,
  accumulatedProducts,
  accumulatedReviewStatsMap,
  onProductsLoaded
}: ProductsGridSectionProps) => {
  const hasAccumulatedProducts = accumulatedProducts && accumulatedProducts.length > 0;
  const content = (() => {
    if (collectionName) {
      return <CollectionProductsGridContent collectionName={collectionName} currentPage={currentPage} productsPerPage={productsPerPage} onProductCountChange={onProductCountChange} onFilterSummaryChange={onFilterSummaryChange} accumulatedProducts={accumulatedProducts} accumulatedReviewStatsMap={accumulatedReviewStatsMap} onProductsLoaded={onProductsLoaded} sortBy={sortBy} />;
    }
    if (searchQuery) {
      return <SearchProductsGridContent searchQuery={searchQuery} filters={filters} sortBy={sortBy} currentPage={currentPage} productsPerPage={productsPerPage} onProductCountChange={onProductCountChange} onFilterSummaryChange={onFilterSummaryChange} accumulatedProducts={accumulatedProducts} accumulatedReviewStatsMap={accumulatedReviewStatsMap} onProductsLoaded={onProductsLoaded} />;
    }
    return <BrowseProductsGridContent category={category} subcategory={subcategory} subSubcategory={subSubcategory} brand={brand} productTag={productTag} filters={filters} sortBy={sortBy} currentPage={currentPage} productsPerPage={productsPerPage} onProductCountChange={onProductCountChange} onFilterSummaryChange={onFilterSummaryChange} accumulatedProducts={accumulatedProducts} accumulatedReviewStatsMap={accumulatedReviewStatsMap} onProductsLoaded={onProductsLoaded} />;
  })();
  const fallback = hasAccumulatedProducts ? <ProductGridContent products={accumulatedProducts} reviewStatsMap={accumulatedReviewStatsMap ?? {}} emptyStateMessage="" sortBy={sortBy} /> : <CollectionsProductGridSkeleton length={12} />;
  return <Box width="100%" data-sentry-element="Box" data-sentry-component="ProductsGridSection" data-sentry-source-file="ProductsGridSection.tsx">
      {hasAccumulatedProducts ? <Suspense fallback={fallback}>{content}</Suspense> : <QueryBoundary loadingFallback={<CollectionsProductGridSkeleton length={12} />}>
          {content}
        </QueryBoundary>}
    </Box>;
};
export default ProductsGridSection;